import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, Globe } from 'lucide-react';
import { countries, detectUserCountry, type Country } from '../data/countries';
import { supabase } from '../lib/supabase';
import FlagIcon from './FlagIcon';

interface CountrySelectorProps {
  onCountrySelected: (countryCode: string, countryName: string) => void;
  onContinue?: () => void;
  isRequired?: boolean;
  title?: string;
  description?: string;
}

export default function CountrySelector({ 
  onCountrySelected, 
  onContinue,
  isRequired = true,
  title = "Select Your Country",
  description = "This helps us apply appropriate regional rules and pricing"
}: CountrySelectorProps) {
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [countrySaved, setCountrySaved] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Auto-detect country on mount
    setDetecting(true);
    detectUserCountry().then(async countryCode => {
      if (countryCode) {
        setDetectedCountry(countryCode);
        setSelectedCountry(countryCode);
        // Auto-save the detected country and mark as saved
        const country = countries.find(c => c.code === countryCode);
        if (country) {
          try {
            // Update auth user metadata like manual selection does
            const { error } = await supabase.auth.updateUser({
              data: {
                country_code: country.code,
                country: country.name,
              }
            });

            if (error) throw error;

            // Call the callback and mark as saved
            onCountrySelected(country.code, country.name);
            setCountrySaved(true);
          } catch (error) {
            console.error('Error updating auto-detected country:', error);
            // Don't mark as saved if there was an error
          }
        }
      }
    }).finally(() => {
      setDetecting(false);
    });
  }, []);

  // Filter countries based on search term
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!countryDropdownOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredCountries.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredCountries.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
            handleCountrySelect(filteredCountries[highlightedIndex]);
          }
          break;
        case 'Escape':
          setCountryDropdownOpen(false);
          setHighlightedIndex(-1);
          setSearchTerm('');
          break;
        default:
          // Handle letter navigation (jump to first country starting with that letter)
          if (e.key.length === 1 && e.key.match(/[a-zA-Z]/)) {
            const letter = e.key.toLowerCase();
            const index = filteredCountries.findIndex(country =>
              country.name.toLowerCase().startsWith(letter)
            );
            if (index >= 0) {
              setHighlightedIndex(index);
              // Scroll to the highlighted item
              setTimeout(() => {
                itemRefs.current[index]?.scrollIntoView({
                  block: 'nearest',
                  behavior: 'smooth'
                });
              }, 0);
            }
          }
          break;
      }
    };

    if (countryDropdownOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [countryDropdownOpen, highlightedIndex, filteredCountries]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (countryDropdownOpen) {
      setHighlightedIndex(-1);
      setSearchTerm('');
    }
  }, [countryDropdownOpen]);

  const handleCountrySelect = async (country: Country) => {
    setSelectedCountry(country.code);
    setCountryDropdownOpen(false);
    setHighlightedIndex(-1);
    setSearchTerm('');
    setErrorMessage(null); // Clear any error messages
    setLoading(true);

    try {
      // Update auth user metadata
      const { error } = await supabase.auth.updateUser({
        data: {
          country_code: country.code,
          country: country.name,
        }
      });

      if (error) throw error;

      // Call the callback
      onCountrySelected(country.code, country.name);
      setCountrySaved(true);
    } catch (error) {
      console.error('Error updating country:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="bg-white/5 border border-white/10 rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe size={32} className="text-white/80" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
          <p className="text-white/60 text-sm">{description}</p>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-white/80 mb-2">
              Country {isRequired && <span className="text-red-500">*</span>}
              {detecting && (
                <span className="ml-2 text-xs text-white/40">Detecting location...</span>
              )}
              {detectedCountry && !detecting && (
                <span className="ml-2 text-xs text-green-400 flex items-center gap-1">
                  <MapPin size={12} />
                  Auto-detected
                </span>
              )}
            </label>
            
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                disabled={loading}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-white/25 flex items-center justify-between hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <span className="flex items-center gap-3">
                  {selectedCountry ? (
                    <>
                      <FlagIcon countryCode={selectedCountry} size="md" />
                      <span>{countries.find(c => c.code === selectedCountry)?.name}</span>
                    </>
                  ) : (
                    <span className="text-white/60">Choose your country</span>
                  )}
                </span>
                <ChevronDown 
                  size={20} 
                  className={`transform transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {countryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-black/95 border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  {/* Search hint */}
                  <div className="px-4 py-2 border-b border-white/10 text-xs text-white/60">
                    💡 Press any letter to jump to countries (e.g., 'N' for Netherlands)
                  </div>
                  
                  {filteredCountries.map((country, index) => (
                    <button
                      key={country.code}
                      ref={(el) => (itemRefs.current[index] = el)}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 text-white transition-colors border-b border-white/5 last:border-b-0 ${
                        index === highlightedIndex 
                          ? 'bg-white/20' 
                          : 'hover:bg-white/10'
                      }`}
                    >
                      <FlagIcon countryCode={country.code} size="md" />
                      <span>{country.name}</span>
                      <span className="text-xs text-white/40 ml-auto">{country.code}</span>
                    </button>
                  ))}
                  
                  {filteredCountries.length === 0 && (
                    <div className="px-4 py-3 text-white/60 text-center">
                      No countries found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Always show continue button, but show error if no country selected */}
          <div className="text-center space-y-3">
            {countrySaved && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
                <MapPin size={16} />
                Country saved successfully
              </div>
            )}
            {errorMessage && (
              <div className="px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {errorMessage}
              </div>
            )}
            {onContinue && (
              <button
                onClick={() => {
                  if (!selectedCountry) {
                    setErrorMessage('Please select your country before continuing.');
                    return;
                  }
                  setErrorMessage(null);
                  onContinue();
                }}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/25"
              >
                Continue
              </button>
            )}
          </div>

          <div className="text-xs text-white/40 text-center">
            This information helps us provide region-appropriate content and comply with local regulations.
          </div>
        </div>
      </div>
    </div>
  );
} 